/**
 * ----------------------------------------------------------------------------
 * ItemChart Component
 * ----------------------------------------------------------------------------
 * Summary:
 * Component for rendering nonconformity counts grouped by item as a donut
 * chart. Fetches the item list and chart data based on the selected date
 * range and checked item filters, groups items beyond the top 10 into an
 * "Others" slice, and displays the results with theme-based colors, labels,
 * tooltips, and a legend.
 *
 * @author Chandan Singh
 * @created September-2026
 * ----------------------------------------------------------------------------
 */
import { observer } from "mobx-react"
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from "recharts"
import "./nc-bar-chart.css"
import "./nc-donut-chart.css"
import { useEffect, useState } from "react"
import { useStore } from "../../../contexts/store-provider"
import noMessageIcon from '../../../assets/Images/no-noconfirmitry.png'
import { FormattedMessage } from "react-intl"
import { formatMessage } from "../../../translations/format-message"
import { TruncatedSvgText } from "./nc-chart-label"

interface ChartProps {
  fromDate: string
  toDate: string
}

// Alternating primary/secondary theme shades so the donut stays in sync with
// the rest of the UI instead of using an unrelated hardcoded palette.
const COLORS = [
  "var(--primary-700)",
  "var(--secondary-600)",
  "var(--primary-500)",
  "var(--secondary-400)",
  "var(--primary-300)",
  "var(--secondary-700)",
  "var(--primary-600)",
  "var(--secondary-300)",
  "var(--primary-400)",
  "var(--secondary-500)",
]
const SHORT_SCREEN_HEIGHT = 700

const renderSliceLabel = ({ x, y, textAnchor, fill, name, value }: any) => (
  <TruncatedSvgText
    text={name}
    suffix={` ${value}`}
    x={x}
    y={y}
    textAnchor={textAnchor}
    fill={fill}
    alignmentBaseline="middle"
    className="recharts-text recharts-pie-label-text"
  />
)

// Below the threshold there isn't enough vertical room for the legend under
// the donut without pushing the chart into a scroll, so the legend moves to
// the left and the donut keeps its usual size on the right instead.
function useIsShortScreen(threshold: number) {
  const [isShort, setIsShort] = useState(() => window.innerHeight <= threshold)

  useEffect(() => {
    const mql = window.matchMedia(`(max-height: ${threshold}px)`)
    const handleChange = () => setIsShort(mql.matches)
    handleChange()
    mql.addEventListener("change", handleChange)
    return () => mql.removeEventListener("change", handleChange)
  }, [threshold])

  return isShort
}

function ItemChart(props: ChartProps) {
  const { fromDate, toDate } = props
  const { nonconformityStore } = useStore()
  const {
    GetItemListService,
    GetByItemsReport,
    getitemChartCheckedValues,
    itemChartData,
    itemList,
    UpdateColumnSelection,
    getNonconformitySummaryDetails
  } = nonconformityStore

  const [searchText, setSearchText] = useState("")
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false)

  const rawList: any[] = itemChartData.ChartReportList ?? []
  const sortedList = [...rawList].sort((a, b) => b.TotalCount - a.TotalCount)

  const allItemsWithColor = sortedList.map((item: any, index: number) => ({
    ...item,
    color: COLORS[index % COLORS.length],
  }))

  const chartData = allItemsWithColor.filter((item: any) => item.TotalCount > 0)

  const legendPayload = allItemsWithColor.map((item: any) => ({
    id: item.DisplayName,
    type: 'square' as const,
    value: `${item.DisplayName}(${item.TotalCount})`,
    color: item.color,
    // Name and count are separate so only the name is cut by the width limit in the CSS.
    formatter: () => (
      <span className="nc-donut-legend-label" title={item.DisplayName}>
        <span className="nc-donut-legend-name">{item.DisplayName}</span>
        <span>({item.TotalCount})</span>
      </span>
    ),
  }))

  const isShortScreen = useIsShortScreen(SHORT_SCREEN_HEIGHT)

  const [isListLoaded, setIsListLoaded] = useState(false)
  useEffect(() => {
    const fetchList = async () => {
      await GetItemListService()
      setIsListLoaded(true)
    }
    fetchList()
  }, [fromDate, toDate])

  useEffect(() => {
    if (!isListLoaded) return;
    if(!getitemChartCheckedValues || !getitemChartCheckedValues.length) return;

    GetByItemsReport(fromDate, toDate, getitemChartCheckedValues)
  }, [isListLoaded, fromDate, toDate, getitemChartCheckedValues])

  // Items are identified by name in both the filter and the report.
  const allNames: string[] = (itemList ?? []).map((name: any) => String(name))

  const allChecked =
    allNames.length > 0 && getitemChartCheckedValues.length === allNames.length

  const filteredNames = allNames.filter((name) =>
    name.toLowerCase().includes(searchText.trim().toLowerCase())
  )

  const toggleItem = (name: string) => {
    const next = getitemChartCheckedValues.includes(name)
      ? getitemChartCheckedValues.filter((x) => x !== name)
      : // keep the original list order
        allNames.filter((x) => x === name || getitemChartCheckedValues.includes(x))
    UpdateColumnSelection(next)
  }

  const toggleAll = () => {
    UpdateColumnSelection(allChecked ? [] : allNames)
  }

  // Totals come from the chart data, so only checked items have a count.
  const getItemTotal = (name: string): number | undefined =>
    rawList.find((r: any) => r.DisplayName === name)?.TotalCount

  return (
    <div className="nc-process-view">
      <div className="nc-stat-row">
        {getNonconformitySummaryDetails.map((card) => (
          <div key={card.labelId} className={`nc-stat-card ${card.variant}`}>
            <div className="nc-stat-label">
              <FormattedMessage id={card.labelId} />
            </div>
            <div className="nc-stat-value">
              {card.value}
              <span className="nc-stat-unit">
                <FormattedMessage id="nc_requests" />
              </span>
            </div>
            <div className="nc-stat-progress">
              {/* Total card shows a plain line; the hidden percent keeps its height equal to the other cards. */}
              <div className="nc-stat-track" style={card.labelId === "nc_total_trays" ? { height: 4, background: "#fff", marginTop: "7px" } : undefined}>
                {card.labelId !== "nc_total_trays" && (
                  <div className="nc-stat-fill" style={{ width: `${card.percent}%` }} />
                )}
              </div>
              {card.labelId !== "nc_total_trays" &&
                <span className="nc-stat-percent">{card.percent}%</span>
              }
            </div>
          </div>
        ))}
      </div>

      <div className={`nc-chart-row${isPanelCollapsed ? " nc-panel-collapsed" : ""}`}>
    <div className={`nc-chart-container nc-chart-item nc-chart-main${isShortScreen ? ' nc-chart-item--legend-left' : ''}`}>
      {!isListLoaded ? null : !getitemChartCheckedValues.length ? (
        <div className="message">
          <div className="d-flex message-area">
            <div className="no-data-img">
              <img
                src={noMessageIcon}
                alt=""
                className="no_message"
              />
            </div>
            <div>
              <h3 className="fw-bold display-6">
                {' '}
                <FormattedMessage id="no_column_selected" /><br />
                <FormattedMessage id="please_select_a_column" />
              </h3>
            </div>
          </div>
        </div>
      ) :
        !rawList.length ? (
          <div className="message">
            <div className="d-flex message-area">
              <div className="no-data-img">
                <img
                  src={noMessageIcon}
                  alt=""
                  className="no_message"
                />
              </div>
              <div>
                <h3 className="fw-bold display-6">
                  {' '}
                  <FormattedMessage id="no_nonconformity" />
                </h3>
              </div>
            </div>
          </div>
        ) : (

          <ResponsiveContainer width="100%" height="100%" className="nc-chart-canvas">
            <PieChart>
              <Pie
                className="nc-donut"
                data={chartData}
                dataKey="TotalCount"
                nameKey="DisplayName"
                cx={isShortScreen ? "68%" : "50%"}
                cy={isShortScreen ? "50%" : "45%"}
                innerRadius={100}
                outerRadius={170}
                paddingAngle={2}
                stroke="var(--white)"
                strokeWidth={2}
                labelLine={chartData.length > 0}
                label={chartData.length > 0 ? renderSliceLabel : false}
              >
                {chartData.map((entry: any) => (
                  <Cell
                    key={entry.DisplayName}
                    fill={entry.color}
                  />
                ))}
              </Pie>
              <Tooltip />
              {isShortScreen ? (
                <Legend payload={legendPayload} layout="vertical" verticalAlign="middle" align="left" />
              ) : (
                <Legend payload={legendPayload} layout="horizontal" verticalAlign="bottom" align="center" />
              )}
            </PieChart>
          </ResponsiveContainer>
        )}
    </div>

        {isPanelCollapsed && (
          <button
            type="button"
            className="nc-legend-strip"
            onClick={() => setIsPanelCollapsed(false)}
            aria-label="Expand item panel"
            title="Expand"
          >
            <span className="nc-legend-strip-icon">&laquo;</span>
            <span className="nc-legend-strip-text">
              <FormattedMessage id="item" />
            </span>
          </button>
        )}

        <aside className="nc-legend-panel">
          <div className="nc-legend-title">
            <FormattedMessage id="item" />
            <button
              type="button"
              className="nc-legend-toggle"
              onClick={() => setIsPanelCollapsed(true)}
              aria-label="Collapse item panel"
              title="Collapse"
            >
              &raquo;
            </button>
          </div>
          <div className="nc-legend-body">
            <div className="nc-legend-all">
              <label className="nc-legend-item">
                <input type="checkbox" checked={allChecked} onChange={toggleAll} />
                <span><FormattedMessage id="select_all_options" /></span>
              </label>
              <div className="nc-legend-search-wrap">
                <input
                  type="text"
                  className="nc-legend-search"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder={formatMessage("search")}
                />
                {searchText && (
                  <button
                    type="button"
                    className="nc-legend-search-clear"
                    onClick={() => setSearchText("")}
                    aria-label="Clear search"
                  >
                    &times;
                  </button>
                )}
              </div>
            </div>
            <div className="nc-legend-list">
              {filteredNames.map((name) => (
                <label key={name} className="nc-legend-item">
                  <input
                    type="checkbox"
                    checked={getitemChartCheckedValues.includes(name)}
                    onChange={() => toggleItem(name)}
                  />
                  <span className="nc-legend-name">{name}</span>
                  <span className="nc-legend-count">
                    {getitemChartCheckedValues.includes(name)
                      ? getItemTotal(name) ?? 0
                      : null}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  )
}

export default observer(ItemChart)
